import {
    AppPlatform,
    LifecycleEventType,
    Release,
    ReleaseCalendarData,
    ReleaseCalendarEventType,
    ReleaseCalendarLifecycleEvent,
    ReleaseStatus,
    ReleaseStepType,
} from '../types';
import { dayjs } from '@/extendedDayjs';

export const generateReleaseCalendarDemoData: (
    platform: AppPlatform,
    demoReleaseId?: string,
) => Record<string, ReleaseCalendarData> = (platform, demoReleaseId) => {
    const releaseCalendar: Record<string, ReleaseCalendarData> = {};
    const releaseCalendarColors = ['blue', 'orange', 'green', 'purple', 'pink', 'teal', 'brown'];
    const releasePilots = ['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com'];
    let currentKickoff = dayjs().subtract(1, 'day');
    let currentSubmission = dayjs().add(2, 'days');
    let currentRelease = dayjs().add(4, 'days');

    if (platform === AppPlatform.Ios) {
        releaseCalendar['1.0.0'] = {
            status: ReleaseStatus.Completed,
            displayVersion: '1.0.0',
            stepLifecycleEvents: [
                {
                    id: '1',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.subtract(1, 'week').toISOString(),
                    endDate: currentKickoff.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '2',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.subtract(1, 'week').toISOString(),
                    endDate: currentSubmission.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '3',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.subtract(1, 'week').toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '4',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.subtract(1, 'week').toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
            ],
            color: releaseCalendarColors[0],
            isReleaseCompleted: true,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[0],
            releaseDescription: 'Major release! Redesign of product page, checkout',
        };

        releaseCalendar['1.1.0'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.1.0',
            stepLifecycleEvents: [
                {
                    id: '01',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '02',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
                },
                {
                    id: '03',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.toISOString(),
                    endDate: currentRelease.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '04',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentRelease.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
            ],
            color: releaseCalendarColors[1],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[1],
            releaseDescription: 'fixes to product page',
        };

        currentKickoff = currentKickoff.add(1, 'week');
        currentSubmission = currentSubmission.add(1, 'week');
        currentRelease = currentRelease.add(1, 'week');
        releaseCalendar['1.2.0'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.2.0',
            stepLifecycleEvents: [
                {
                    id: '11',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '12',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '13',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.toISOString(),
                    endDate: currentRelease.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '14',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentRelease.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[2],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[2],
            releaseDescription:
                'Couple big updates planned for this one: adding a new payment confirmation screen, adding functionality to save multiple items, sharing functionality.',
        };
    } else if (platform === AppPlatform.Android) {
        releaseCalendar['1.0.0'] = {
            status: ReleaseStatus.Completed,
            displayVersion: '1.0.0',
            stepLifecycleEvents: [
                {
                    id: '21',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.subtract(1, 'week').toISOString(),
                    endDate: currentKickoff.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '22',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.subtract(1, 'week').toISOString(),
                    endDate: currentSubmission.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
                },
                {
                    id: '23',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.subtract(1, 'week').toISOString(),
                    endDate: currentSubmission.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[0],
            isReleaseCompleted: true,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[0],
            releaseDescription: 'Major release! Redesign of product page, checkout',
        };

        releaseCalendar['1.1.0'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.1.0',
            stepLifecycleEvents: [
                {
                    id: '31',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '32',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '33',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[1],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[1],
            releaseDescription: 'fixes to product page',
        };

        currentKickoff = currentKickoff.add(1, 'week');
        currentSubmission = currentSubmission.add(1, 'week');
        releaseCalendar['1.2.0'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.2.0',
            stepLifecycleEvents: [
                {
                    id: '41',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '42',
                    stepId: ReleaseStepType.Submission,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
                    startDate: currentSubmission.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '43',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentSubmission.toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[2],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[2],
            releaseDescription:
                'Couple big updates planned for this one: adding a new payment confirmation screen, adding functionality to save multiple items, sharing functionality.',
        };
    } else if (platform === AppPlatform.ReactNativeOTA) {
        releaseCalendar['1.0.1'] = {
            status: ReleaseStatus.Completed,
            displayVersion: '1.0.1',
            stepLifecycleEvents: [
                {
                    id: '51',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '52',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.subtract(1, 'week').toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '53',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[0],
            isReleaseCompleted: true,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[0],
            releaseDescription: 'Major release! Redesign of product page, checkout',
        };

        releaseCalendar['1.0.2'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.0.2',
            stepLifecycleEvents: [
                {
                    id: '61',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '62',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.subtract(1, 'week').toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '63',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[1],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[1],
            releaseDescription: 'fixes to product page',
        };

        currentKickoff = currentKickoff.add(1, 'week');
        currentRelease = currentRelease.add(1, 'week');
        releaseCalendar['1.0.3'] = {
            status: ReleaseStatus.Active,
            displayVersion: '1.0.3',
            stepLifecycleEvents: [
                {
                    id: '71',
                    stepId: ReleaseStepType.Kickoff,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentKickoff.toISOString(),
                    targetDateSetByCadence: true,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '72',
                    stepId: ReleaseStepType.ReleaseCandidate,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
                    startDate: currentRelease.subtract(1, 'week').toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
                {
                    id: '73',
                    stepId: ReleaseStepType.ReleaseCycle,
                    lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
                    startDate: currentKickoff.toISOString(),
                    endDate: currentRelease.subtract(1, 'week').toISOString(),
                    targetDateSetByCadence: false,
                    dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
                },
            ],
            color: releaseCalendarColors[2],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[2],
            releaseDescription:
                'Couple big updates planned for this one: adding a new payment confirmation screen, adding functionality to save multiple items, sharing functionality.',
        };
    }

    if (demoReleaseId) {
        const releaseLevelCalendar: Record<string, ReleaseCalendarData> = {};
        const releaseId = demoReleaseId.split(':')[1];
        releaseLevelCalendar[releaseId] = releaseCalendar[releaseId];
        return releaseLevelCalendar;
    }

    const baseVersion = '1.';
    const otaBaseVersion = '1.0.';
    let currentVersion = 3;
    let currentOtaVersion = 4;
    let currentCalendarColor = 3;
    let currentPilot = 0;

    for (let i = 0; i < 20; i++) {
        let releaseVersion = baseVersion + currentVersion.toString() + '.0';
        currentKickoff = currentKickoff.add(1, 'week');
        currentSubmission = currentSubmission.add(1, 'week');
        currentRelease = currentRelease.add(1, 'week');
        const stepLifecycleEvents: ReleaseCalendarLifecycleEvent[] = [];

        const kickoffEvent: ReleaseCalendarLifecycleEvent = {
            id: `kickoff-${i}`,
            stepId: ReleaseStepType.Kickoff,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
            startDate: currentKickoff.toISOString(),
            endDate: currentKickoff.toISOString(),
            targetDateSetByCadence: false,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
        };

        const submissionEvent: ReleaseCalendarLifecycleEvent = {
            id: `submission-${i}`,
            stepId: ReleaseStepType.Submission,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
            startDate: currentSubmission.toISOString(),
            endDate: currentSubmission.toISOString(),
            targetDateSetByCadence: false,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
        };

        const releaseEvent: ReleaseCalendarLifecycleEvent = {
            id: `release-${i}`,
            stepId: ReleaseStepType.ReleaseCandidate,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
            startDate: currentRelease.toISOString(),
            endDate: currentRelease.toISOString(),
            targetDateSetByCadence: true,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
        };
        const ReleaseCycleEventVariant1: ReleaseCalendarLifecycleEvent = {
            id: `releaseCycle1-${i}`,
            stepId: ReleaseStepType.ReleaseCandidate,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
            startDate: currentKickoff.toISOString(),
            endDate: currentRelease.toISOString(),
            targetDateSetByCadence: true,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
        };
        const ReleaseCycleEventVariant2: ReleaseCalendarLifecycleEvent = {
            id: `releaseCycle2-${i}`,
            stepId: ReleaseStepType.ReleaseCandidate,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
            startDate: currentKickoff.toISOString(),
            endDate: currentSubmission.toISOString(),
            targetDateSetByCadence: true,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate,
        };

        if (platform === AppPlatform.Ios) {
            stepLifecycleEvents.push(kickoffEvent);
            stepLifecycleEvents.push(submissionEvent);
            stepLifecycleEvents.push(releaseEvent);
            stepLifecycleEvents.push(ReleaseCycleEventVariant1);
        } else if (platform === AppPlatform.Android) {
            stepLifecycleEvents.push(kickoffEvent);
            stepLifecycleEvents.push(submissionEvent);
            stepLifecycleEvents.push(ReleaseCycleEventVariant2);
        } else if (platform === AppPlatform.ReactNativeOTA) {
            releaseVersion = otaBaseVersion + currentOtaVersion.toString();
            stepLifecycleEvents.push(kickoffEvent);
            stepLifecycleEvents.push(releaseEvent);
            stepLifecycleEvents.push(ReleaseCycleEventVariant1);
        }

        releaseCalendar[releaseVersion] = {
            status: ReleaseStatus.Predicted,
            displayVersion: releaseVersion,
            stepLifecycleEvents: stepLifecycleEvents,
            color: releaseCalendarColors[currentCalendarColor],
            isReleaseCompleted: false,
            canReleaseBeSubmitted: false,
            scheduledAutomationsPaused: false,
            isHotfix: false,
            releasePilotId: releasePilots[currentPilot],
        };

        currentVersion++;
        currentOtaVersion++;
        if (currentCalendarColor >= releaseCalendarColors.length - 1) {
            currentCalendarColor = 0;
        } else {
            currentCalendarColor++;
        }

        if (currentPilot >= 2) {
            currentPilot = 0;
        } else {
            currentPilot++;
        }
    }

    return releaseCalendar;
};

export const generateReleaseCalendarForWorkflowRun = (workflowRun: Release): Record<string, ReleaseCalendarData> => {
    const releaseCalendar: Record<string, ReleaseCalendarData> = {};
    const releaseCalendarColors = ['blue', 'orange', 'green', 'purple', 'pink', 'teal', 'brown'];
    const releasePilots = ['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com'];

    const stepLifecycleEvents: ReleaseCalendarData['stepLifecycleEvents'] = [];

    // Add kickoff event if available
    if (workflowRun.kickedOffAt) {
        stepLifecycleEvents.push({
            id: `kickoff-${workflowRun.version}`,
            stepId: ReleaseStepType.Kickoff,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
            startDate: workflowRun.kickedOffAt,
            endDate: workflowRun.kickedOffAt,
            targetDateSetByCadence: false,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
        });
    } else if (workflowRun.targetKickoffDate) {
        stepLifecycleEvents.push({
            id: `kickoff-${workflowRun.version}`,
            stepId: ReleaseStepType.Kickoff,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeKickoff,
            startDate: workflowRun.targetKickoffDate,
            endDate: workflowRun.targetKickoffDate,
            targetDateSetByCadence: true,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
        });
    }

    // Add submission event if available
    if (workflowRun.submittedAt) {
        stepLifecycleEvents.push({
            id: `submission-${workflowRun.version}`,
            stepId: ReleaseStepType.Submission,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
            startDate: workflowRun.submittedAt,
            endDate: workflowRun.submittedAt,
            targetDateSetByCadence: false,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
        });
    } else if (workflowRun.targetSubmitDate) {
        stepLifecycleEvents.push({
            id: `submission-${workflowRun.version}`,
            stepId: ReleaseStepType.Submission,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeSubmit,
            startDate: workflowRun.targetSubmitDate,
            endDate: workflowRun.targetSubmitDate,
            targetDateSetByCadence: true,
            dateType:
                workflowRun.isKickedOff ?
                    ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate
                :   ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
        });
    }

    // Add release event if available (using RenderedAt as a proxy for release date)
    if (workflowRun.RenderedAt) {
        stepLifecycleEvents.push({
            id: `release-${workflowRun.version}`,
            stepId: ReleaseStepType.ReleaseCandidate,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
            startDate: workflowRun.RenderedAt,
            endDate: workflowRun.RenderedAt,
            targetDateSetByCadence: false,
            dateType: ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate,
        });
    } else if (workflowRun.targetReleaseDate) {
        stepLifecycleEvents.push({
            id: `release-${workflowRun.version}`,
            stepId: ReleaseStepType.ReleaseCandidate,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeRelease,
            startDate: workflowRun.targetReleaseDate,
            endDate: workflowRun.targetReleaseDate,
            targetDateSetByCadence: true,
            dateType:
                workflowRun.isSubmitted ?
                    ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate
                :   ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
        });
    }

    // Add release cycle event
    const cycleStartDate = workflowRun.kickedOffAt || workflowRun.targetKickoffDate;
    const cycleEndDate = workflowRun.RenderedAt || workflowRun.targetReleaseDate || workflowRun.targetSubmitDate;

    if (cycleStartDate && cycleEndDate) {
        stepLifecycleEvents.push({
            id: `cycle-${workflowRun.version}`,
            stepId: ReleaseStepType.ReleaseCycle,
            lifecycleEventType: LifecycleEventType.LifecycleEventTypeReleaseCycle,
            startDate: cycleStartDate,
            endDate: cycleEndDate,
            targetDateSetByCadence: false,
            dateType:
                workflowRun.status === ReleaseStatus.Completed ?
                    ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate
                :   ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate,
        });
    }

    releaseCalendar[workflowRun.version] = {
        status: workflowRun.status,
        displayVersion: workflowRun.version,
        stepLifecycleEvents,
        color: releaseCalendarColors[0],
        isReleaseCompleted: workflowRun.status === ReleaseStatus.Completed,
        canReleaseBeSubmitted: workflowRun.canBeSubmitted,
        scheduledAutomationsPaused: workflowRun.scheduledAutomationsPaused,
        isHotfix: workflowRun.sourceVersion !== undefined, // if sourceVersion exists, it's a rollback/hotfix
        releasePilotId: workflowRun.releasePilotId || releasePilots[0],
        releaseDescription: workflowRun.releaseSummaryMessage?.message || '',
    };

    return releaseCalendar;
};
